import React from "react";

import { useAuthenticateMutation } from "features/api/auth/authApiSlice";

import { Formik } from "formik";
import { Form as FormControl, ThemeProvider } from "react-bootstrap";
import { useTranslation } from "react-i18next";

import BootstrapForm from "react-bootstrap/Form";
import InputGroup from "react-bootstrap/InputGroup";
import { theme } from "configs/theme";
import Button from "react-bootstrap/Button";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";

import * as Yup from "yup";
import i18nInstance from "i18n";

import {
  PhoneVerificationProps,
  PhoneVerificationValues,
} from "../types/types";
import "./PhoneVerification.scss";

const validationSchema = Yup.object({
  email: Yup.string().required(i18nInstance.t("חובה להזין כתובת מייל")),
  password: Yup.string()
    .required(i18nInstance.t("חובה להזין סיסמה"))
    .min(6, i18nInstance.t("הסיסמה צריכה להיות באורך של 6 תווים לפחות")),
});

const PhoneVerification: React.FC<PhoneVerificationProps> = ({
  setView,
  setPhoneVerificationResponse,
}) => {
  const initialValues: PhoneVerificationValues = { email: "", password: "" };

  const { i18n, t } = useTranslation();
  const [authenticate] = useAuthenticateMutation();

  const handleVerifyPhoneNumber = async (
    values: PhoneVerificationValues,
    {
      setSubmitting,
      setFieldError,
    }: {
      setSubmitting: (isSubmitting: boolean) => void;
      setFieldError: (field: string, message: string) => void;
    }
  ) => {
    setSubmitting(true);
    authenticate({
      Email: values.email,
      Password: values.password,
    })
      .then((originalPromiseResult: { error?: string }) => {
        // handle result here
        if (!originalPromiseResult?.error) {
          setPhoneVerificationResponse({
            user: {
              email: values.email,
            },
          });
          setSubmitting(false);
          setView("otpConfirmation");
        } else {
          throw originalPromiseResult.error;
        }
      })
      .catch((err: { data?: { message?: string }; status: number }) => {
        console.error(err);
        if (err.status === 401 || err.status === 400) {
          // if error status is 401 / 400, then the server error message will be displayed
          setFieldError("password", err.data.message);
          setSubmitting(false);
        } else {
          setFieldError("password", "Login Error");
          setSubmitting(false);
        }
      });
    setSubmitting(false);
  };

  return (
    <ThemeProvider dir={i18n.dir()} theme={theme}>
      <div className="d-flex justify-content-center align-items-center container rtl">
        <div className="card py-5 px-3">
          <h1 className="m-0 text-right">{t("loginTitle")}</h1>
          <Formik
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={handleVerifyPhoneNumber}
          >
            {({
              isSubmitting,
              handleSubmit,
              handleChange,
              values,
              touched,
              isValid,
              errors,
            }) => {
              return (
                <FormControl
                  className="mt-5"
                  noValidate
                  onSubmit={handleSubmit}
                >
                  <Row className="mb-3">
                    <InputGroup hasValidation>
                      <FormControl.Group
                        as={Col}
                        md="10"
                        controlId="validationEmail"
                      >
                        <FormControl.Label className="text-right">
                          {t("emailAddressLabel")}
                        </FormControl.Label>

                        <FormControl.Control
                          isInvalid={!!errors?.email}
                          type="email"
                          aria-describedby="emailHelpBlock"
                          disabled={isSubmitting}
                          placeholder={t("emailAddressPlaceholder")}
                          size="lg"
                          name="email"
                          value={values.email}
                          onChange={handleChange}
                          isValid={touched.email && !errors.email}
                        />
                        {errors?.email && (
                          <BootstrapForm.Control.Feedback type="invalid">
                            {errors.email}
                          </BootstrapForm.Control.Feedback>
                        )}
                      </FormControl.Group>
                    </InputGroup>
                  </Row>
                  <Row className="mb-3">
                    <InputGroup hasValidation>
                      <FormControl.Group
                        as={Col}
                        md="10"
                        controlId="validationPassword"
                      >
                        <FormControl.Label className="text-right">
                          {t("passWordPlaceHolder")}
                        </FormControl.Label>

                        <FormControl.Control
                          isInvalid={!!errors?.password}
                          type="password"
                          aria-describedby="passwordHelpBlock"
                          disabled={isSubmitting}
                          placeholder=""
                          size="lg"
                          name="password"
                          value={values.password}
                          onChange={handleChange}
                          isValid={touched.password && !errors.password}
                        />
                        {errors?.password && (
                          <BootstrapForm.Control.Feedback type="invalid">
                            {errors.password}
                          </BootstrapForm.Control.Feedback>
                        )}
                      </FormControl.Group>
                    </InputGroup>
                  </Row>

                  <Button
                    variant="primary"
                    type="submit"
                    disabled={isSubmitting || !isValid}
                  >
                    {isSubmitting ? t("loggingIn") : t("loginTitle")}{" "}
                  </Button>
                </FormControl>
              );
            }}
          </Formik>
        </div>
      </div>
    </ThemeProvider>
  );
};

export default PhoneVerification;
